// LAP-646 — Social Dashboard : 5 sections (Vue globale, Répartition, Évolution, Relations clés, Insights)
// LAP-XXX — Social Dashboard v2 : +1 section "Relances suggérées" (contacts ≥ 4★
//          sans interaction depuis seuil utilisateur).
import React, { useEffect, useState } from 'react'
import { Box, Container, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { makeGetRequest } from 'utils/api'
import CardShell from '../Investments/_shared/CardShell'
import { invTheme } from '../Investments/_shared/theme'
import OverviewCard from './cards/OverviewCard'
import RepartitionCard from './cards/RepartitionCard'
import EvolutionCard from './cards/EvolutionCard'
import KeyRelationsCard from './cards/KeyRelationsCard'
import SuggestionsCard from './cards/SuggestionsCard' // LAP-XXX
import InsightsCard from './cards/InsightsCard'
import AdvisorCard from './cards/AdvisorCard' // LAP-768

export default function SocialDashboard() {
  const { t } = useTranslation('layout')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchDashboard = React.useCallback(async () => {
    try {
      const res = await makeGetRequest('/api/Social/dashboard/', {}, true)
      if (res.status === 200) setData(res.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const res = await makeGetRequest('/api/Social/dashboard/', {}, true)
        if (!cancelled && res.status === 200) setData(res.data)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  // LAP-XXX — quand l'utilisateur change le seuil, on relance le dashboard
  // pour récupérer la nouvelle liste suggested_tasks calculée côté back.
  const onThresholdChange = React.useCallback(() => {
    fetchDashboard()
  }, [fetchDashboard])

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <Container maxWidth="xl">
        <Box sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>
            {t('social.title', 'Social Dashboard')}
          </Box>
          <Box sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            {t('social.subtitle', 'Tes dynamiques relationnelles en un coup d’œil')}
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, pb: 6, mt: 1 }}>
          {/* LAP-768 — Ligne 0 — Conseiller relationnel (pleine largeur, porte
              d'entrée vers l'analyse complète, miroir des autres tableaux de bord) */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CardShell
                accent={invTheme.accents.startups}
                minHeight={220}
                detailTo="/conseiller-social"
              >
                <AdvisorCard data={data} loading={loading} />
              </CardShell>
            </Grid>
          </Grid>
          {/* Ligne 1 — Vue globale (large) + Répartition (compact) */}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={7} lg={7}>
              <CardShell accent={invTheme.accents.market} minHeight={260}>
                <OverviewCard data={data?.overview} loading={loading} />
              </CardShell>
            </Grid>
            <Grid item xs={12} md={5} lg={5}>
              <CardShell accent={invTheme.accents.repartition} minHeight={260}>
                <RepartitionCard data={data?.repartition} loading={loading} />
              </CardShell>
            </Grid>
          </Grid>
          {/* Ligne 2 — Évolution (large) + Relations clés (compact) */}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={7} lg={7}>
              <CardShell accent={invTheme.accents.portfolio} minHeight={380}>
                <EvolutionCard data={data?.evolution} loading={loading} />
              </CardShell>
            </Grid>
            <Grid item xs={12} md={5} lg={5}>
              <CardShell accent={invTheme.accents.target} minHeight={380}>
                <KeyRelationsCard data={data?.key_relations} loading={loading} />
              </CardShell>
            </Grid>
          </Grid>
          {/* LAP-XXX — Ligne 3 — Suggestions de relances (pleine largeur) */}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <CardShell accent={invTheme.accents.target} minHeight={260}>
                <SuggestionsCard
                  data={data?.suggested_tasks}
                  meta={data?.suggested_tasks_meta}
                  loading={loading}
                  onThresholdChange={onThresholdChange}
                />
              </CardShell>
            </Grid>
          </Grid>
          {/* Ligne 4 — Insights (pleine largeur) */}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <CardShell accent={invTheme.accents.advice} minHeight={200}>
                <InsightsCard data={data?.insights} loading={loading} />
              </CardShell>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}
